import { API_BASE } from '@/shared/services/api';
import OpenAI from 'openai';

const BACKEND_URL = API_BASE;

export class AIService {
  constructor() {
    this.apiKey = null;
  }

  async initialize() {
    try {
      const response = await fetch(`${BACKEND_URL}/api/llm-key`);
      const data = await response.json();
      this.apiKey = data.key;
      this.client = new OpenAI({
        apiKey: this.apiKey,
        dangerouslyAllowBrowser: true
      });
      return true;
    } catch (error) {
      console.error('Failed to initialize AI service:', error);
      return false;
    }
  }

  async optimizeRoute(shipments, constraints = {}) {
    if (!this.client) {
      await this.initialize();
    }

    try {
      const prompt = `Given the following shipments, optimize the delivery route considering:
- Number of shipments: ${shipments.length}
- Constraint details: ${JSON.stringify(constraints)}

Shipments:
${shipments.map(s => `- ${s.trackingId}: ${s.deliveryAddress}`).join('\\n')}

Provide an optimized route order and estimated time savings.`;

      const response = await this.client.chat.completions.create({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'You are a logistics optimization expert. Provide practical route optimization suggestions.'
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.7,
        max_tokens: 500
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('Route optimization error:', error);
      return 'AI optimization temporarily unavailable. Using standard routing algorithm.';
    }
  }

  async predictETA(routeData) {
    if (!this.client) {
      await this.initialize();
    }

    try {
      const prompt = `Based on the following route information, predict accurate ETA:
- Total distance: ${routeData.distance}
- Stops: ${routeData.stops}
- Time of day: ${routeData.timeOfDay}
- Traffic conditions: ${routeData.traffic || 'moderate'}
- Weather: ${routeData.weather || 'clear'}

Provide ETA prediction with confidence level.`;

      const response = await this.client.chat.completions.create({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'You are a delivery time prediction expert. Provide realistic ETA estimates.'
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.5,
        max_tokens: 200
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('ETA prediction error:', error);
      return null;
    }
  }

  async analyzeException(exceptionData) {
    if (!this.client) {
      await this.initialize();
    }

    try {
      const prompt = `Analyze this delivery exception and provide recommendations:
Exception Type: ${exceptionData.type}
Details: ${exceptionData.details}
Previous attempts: ${exceptionData.attempts}
Customer history: ${exceptionData.customerHistory || 'none'}

Suggest the best course of action.`;

      const response = await this.client.chat.completions.create({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'You are an expert in handling delivery exceptions. Provide actionable recommendations.'
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.6,
        max_tokens: 300
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('Exception analysis error:', error);
      return null;
    }
  }
}

export const aiService = new AIService();